import type {
  EntityFieldType,
  FieldConfig,
  FieldTypeConfiguration,
} from "@/pages/digital-files/entity/config/type";

type ValidationErrors = Record<string, string>;

export const collectDatacenterValuesForFieldData = (
  fieldConfig: FieldTypeConfiguration | null | undefined,
  formValues: Record<string, unknown>,
) => {
  const datacenterValues: Record<string, unknown> = {};
  let optionsTable: string | undefined;
  let optionsColumn: string | undefined;
  let optionsRootTable: string | undefined;
  let optionsRootColumn: string | undefined;

  if (!fieldConfig) {
    return {
      datacenterValues,
      optionsTable,
      optionsColumn,
      optionsRootTable,
      optionsRootColumn,
    };
  }

  fieldConfig.sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.type !== "datacenter") {
        return;
      }

      const tableKey = `${field.key}_table`;
      const columnKey = `${field.key}_column`;

      if (field.key === "valueDatacenter") {
        if (formValues[tableKey]) {
          optionsTable = formValues[tableKey] as string;
        }
        if (formValues[columnKey]) {
          optionsColumn = formValues[columnKey] as string;
        }
        return;
      }

      if (field.key === "options_root_datacenter") {
        if (formValues[tableKey]) {
          optionsRootTable = formValues[tableKey] as string;
        }
        if (formValues[columnKey]) {
          optionsRootColumn = formValues[columnKey] as string;
        }
        return;
      }

      if (formValues[tableKey]) {
        datacenterValues[tableKey] = formValues[tableKey];
      }
      if (formValues[columnKey]) {
        datacenterValues[columnKey] = formValues[columnKey];
      }
    });
  });

  return {
    datacenterValues,
    optionsTable,
    optionsColumn,
    optionsRootTable,
    optionsRootColumn,
  };
};

export const getNormalizedFieldType = (currentType: EntityFieldType) =>
  currentType === "local_list" ? "select" : currentType;

const buildDropdownButtonConfiguration = ({
  hasDropdownButton,
  buttonPosition,
  valueSource,
  optionsTable,
  optionsColumn,
  localListItems,
}: {
  hasDropdownButton: unknown;
  buttonPosition: unknown;
  valueSource: unknown;
  optionsTable?: string;
  optionsColumn?: string;
  localListItems: string[];
}): Record<string, unknown> => {
  if (!hasDropdownButton) return {};

  return {
    ...(buttonPosition ? { buttonPosition } : {}),
    ...(valueSource ? { options_source: valueSource } : {}),
    ...(valueSource === "database" && {
      ...(optionsTable && { options_table: optionsTable }),
      ...(optionsColumn && { options_column: optionsColumn }),
    }),
    ...(valueSource === "manual" &&
      localListItems.length > 0 && {
        options_dropdown: localListItems,
      }),
  };
};

const buildOptionsRootConfiguration = ({
  optionsRootSource,
  optionsRootTable,
  optionsRootColumn,
  optionsRootList,
}: {
  optionsRootSource: unknown;
  optionsRootTable?: string;
  optionsRootColumn?: string;
  optionsRootList: unknown;
}): Record<string, unknown> => ({
  ...(optionsRootSource ? { options_root_source: optionsRootSource } : {}),
  ...(optionsRootSource === "database" && {
    ...(optionsRootTable && { options_root_table: optionsRootTable }),
    ...(optionsRootColumn && { options_root_column: optionsRootColumn }),
  }),
  ...(optionsRootSource === "manual" &&
    Array.isArray(optionsRootList) &&
    optionsRootList.length > 0 && { options: optionsRootList }),
});

export const buildFieldConfiguration = ({
  formConfiguration,
  normalizedType,
  currentType,
  finalDocumentType,
  hasDropdownButton,
  buttonPosition,
  valueSource,
  optionsTable,
  optionsColumn,
  optionsRootSource,
  optionsRootTable,
  optionsRootColumn,
  optionsRootList,
  datacenterValues,
  localListItems,
}: {
  formConfiguration: unknown;
  normalizedType: string;
  currentType: EntityFieldType;
  finalDocumentType: unknown;
  hasDropdownButton: unknown;
  buttonPosition: unknown;
  valueSource: unknown;
  optionsTable?: string;
  optionsColumn?: string;
  optionsRootSource: unknown;
  optionsRootTable?: string;
  optionsRootColumn?: string;
  optionsRootList: unknown;
  datacenterValues: Record<string, unknown>;
  localListItems: string[];
}): Record<string, unknown> => {
  const dropdownButtonConfiguration = buildDropdownButtonConfiguration({
    hasDropdownButton,
    buttonPosition,
    valueSource,
    optionsTable,
    optionsColumn,
    localListItems,
  });

  const optionsRootConfiguration = buildOptionsRootConfiguration({
    optionsRootSource,
    optionsRootTable,
    optionsRootColumn,
    optionsRootList,
  });

  return {
    ...(formConfiguration as Record<string, unknown>),
    type: normalizedType,
    ...(currentType === "identification" && finalDocumentType
      ? { documentType: finalDocumentType }
      : {}),
    ...(hasDropdownButton === undefined
      ? {}
      : { buttonEnabled: hasDropdownButton }),
    ...dropdownButtonConfiguration,
    ...optionsRootConfiguration,
    ...datacenterValues,
  };
};

export const validateLocalListItemsRequired = ({
  field,
  value,
  formValues,
  localListItems,
  newErrors,
}: {
  field: FieldConfig;
  value: unknown;
  formValues: Record<string, unknown>;
  localListItems: string[];
  newErrors: ValidationErrors;
}) => {
  const addRequiredError = () => {
    newErrors[field.key] = `${field.label} es requerido`;
  };

  if (field.key === "localList") {
    if (formValues.valueSource !== "manual" || localListItems.length > 0) {
      return;
    }
    addRequiredError();
    return;
  }

  if (field.key === "options_root_list") {
    if (formValues.options_root_source !== "manual") {
      return;
    }

    const items = Array.isArray(value) ? value : [];
    if (items.length === 0) {
      addRequiredError();
    }
    return;
  }

  const items = Array.isArray(value) ? value : [];
  if (items.length === 0) {
    addRequiredError();
  }
};

export const validateDataCenterRequired = ({
  field,
  formValues,
  newErrors,
}: {
  field: FieldConfig;
  formValues: Record<string, unknown>;
  newErrors: ValidationErrors;
}) => {
  const tableKey = `${field.key}_table`;
  const columnKey = `${field.key}_column`;

  if (field.key === "valueDatacenter") {
    const source = formValues.valueSource;
    if (source === "database") {
      if (!formValues[tableKey] || !formValues[columnKey]) {
        newErrors[field.key] = `${field.label} es requerido`;
      }
    }
  } else if (field.key === "options_root_datacenter") {
    const source = formValues.options_root_source;
    if (source === "database") {
      if (!formValues[tableKey] || !formValues[columnKey]) {
        newErrors[field.key] = `${field.label} es requerido`;
      }
    }
  } else if (!formValues[tableKey] || !formValues[columnKey]) {
    newErrors[field.key] = `${field.label} es requerido`;
  }
};

export const validateFileTypesRequired = (
  field: FieldConfig,
  value: unknown,
  newErrors: ValidationErrors,
) => {
  const items = Array.isArray(value) ? value : [];
  if (items.length === 0) {
    newErrors[field.key] = `${field.label} es requerido`;
  }
};

export const validateGenericRequired = (
  field: FieldConfig,
  value: unknown,
  newErrors: ValidationErrors,
) => {
  if (value === undefined || value === null) {
    newErrors[field.key] = `${field.label} es requerido`;
    return;
  }

  if (
    typeof value === "string" &&
    value === "" &&
    field.defaultValue === undefined
  ) {
    newErrors[field.key] = `${field.label} es requerido`;
  }
};

export const validateNumericCustomRules = (
  field: FieldConfig,
  value: unknown,
  newErrors: ValidationErrors,
) => {
  if (typeof value !== "number" || !field.validation) return;

  if (field.validation.min !== undefined && value < field.validation.min) {
    newErrors[field.key] = `El valor mínimo es ${field.validation.min}`;
  }

  if (field.validation.max !== undefined && value > field.validation.max) {
    newErrors[field.key] = `El valor máximo es ${field.validation.max}`;
  }
};

export const validateStringCustomRules = (
  field: FieldConfig,
  value: unknown,
  newErrors: ValidationErrors,
) => {
  if (typeof value !== "string" || !field.validation) return;

  if (
    field.validation.minLength !== undefined &&
    value.length < field.validation.minLength
  ) {
    newErrors[field.key] = `Mínimo ${field.validation.minLength} caracteres`;
  }

  if (
    field.validation.maxLength !== undefined &&
    value.length > field.validation.maxLength
  ) {
    newErrors[field.key] = `Máximo ${field.validation.maxLength} caracteres`;
  }

  if (field.validation.pattern) {
    const regex = new RegExp(field.validation.pattern);
    if (!regex.test(value)) {
      newErrors[field.key] = "El formato no es válido";
    }
  }
};

export const validateCustomRules = (
  field: FieldConfig,
  value: unknown,
  newErrors: ValidationErrors,
) => {
  if (
    !field.validation ||
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return;
  }

  validateNumericCustomRules(field, value, newErrors);
  validateStringCustomRules(field, value, newErrors);

  const customError = field.validation.custom?.(value);
  if (customError) {
    newErrors[field.key] = customError;
  }
};
